import React, {useEffect, useState} from 'react';
import {useNavigate, useOutletContext} from 'react-router-dom';
import {findTask} from '../../api/jobApi';
import {JobPostingData} from "../../interface/JobPostingData";
import FilterModal from '../../components/common/FilterModal';
import {defaultAlert} from '../../utils/alert';


interface OutletContextType {
    workTypeFilter: string;
    setLoading: (loading: boolean) => void;
}

export default function FindTaskPage() {
    const {workTypeFilter, setLoading} = useOutletContext<OutletContextType>();
    const navigate = useNavigate();
    const [searchKeyword, setSearchKeyword] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [jobList, setJobList] = useState<JobPostingData[]>([]);

    // 필터 상태
    const [filters, setFilters] = useState({
        serviceType: '',
        workType: '',
        carePlaceType: '',
        startDate: '',
        location: '',
        gender: '',
        order: ''
    });

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            workType: workTypeFilter === 'ALL' ? '' : workTypeFilter
        }));
    }, [workTypeFilter, setLoading]);

    useEffect(() => {
        fetchJobList();
    }, [filters]);

    const fetchJobList = async () => {
        try {
            const params = {
                keyword: searchKeyword,
                ...filters
            };
            const response = await findTask(params);
            setJobList(response.data);
        } catch (error) {
            console.error('Failed to fetch job list:', error);
            defaultAlert('업무 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleDetailClick = (jobPostingCode: string) => {
        navigate(`/announcement/find/${jobPostingCode}`);
    };

    const handleSearch = () => {
        fetchJobList();
    };

    const handleFilterApply = () => {
        fetchJobList();
        setIsModalOpen(false);
    };

    const handleFilterReset = () => {
        setFilters({
            serviceType: '',
            workType: '',
            carePlaceType: '',
            startDate: '',
            location: '',
            gender: '',
            order: ''
        });
    };

    const scrollToTop = () => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    };
    const getDeadlineDisplay = (job: JobPostingData): string => {
        let deadline: Date;

        if (job.workType === 'HOURLY') {
            if (!job.careStartDate || !job.workStartTime) return '마감';

            const [year, month, day] = job.careStartDate.split('-').map(Number);
            const [hour, minute] = job.workStartTime.split(':').map(Number);
            deadline = new Date(year, month - 1, day, hour, minute, 0);

        } else if (job.workType === 'DAILY') {
            if (!job.careStartDate) return '마감';

            const [year, month, day] = job.careStartDate.split('-').map(Number);
            deadline = new Date(year, month - 1, day, 0, 0, 0); // 해당 날짜 자정

        } else {
            return '마감';
        }

        const now = new Date();
        const diffMs = deadline.getTime() - now.getTime();

        if (diffMs <= 0) return '마감';

        const diffMin = Math.floor(diffMs / (1000 * 60));
        const diffHour = Math.floor(diffMin / 60);
        const remainMin = diffMin % 60;

        if (diffHour >= 24) {
            const diffDay = Math.floor(diffHour / 24);
            const remainHour = diffHour % 24;
            return remainHour > 0
                ? `${diffDay}일 ${remainHour}시간 후 마감`
                : `${diffDay}일 후 마감`;
        }
        if (diffHour > 0) {
            return remainMin > 0
                ? `${diffHour}시간 ${remainMin}분 후 마감`
                : `${diffHour}시간 후 마감`;
        }
        return `${diffMin}분 후 마감`;
    };
    return (
        <div>
            <main>
                <button type="button" className="top-btn" onClick={scrollToTop}>
                    <i className="fa-solid fa-chevron-up"></i>
                    <span className="blind">맨위로</span>
                </button>
                <section className="bg-white">
                    <div className="inner-top">
                        <div className="search-task-wrap">
                            <div className="search-box">
                                <input
                                    type="text"
                                    name="keyWord"
                                    id="keyWord"
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    placeholder="장소 또는 공고번호를 입력해주세요"
                                />
                                <button
                                    type="button"
                                    className="del"
                                    onClick={() => setSearchKeyword('')}
                                >
                                    <i className="fa-solid fa-circle-xmark"></i>
                                </button>
                                <button
                                    type="button"
                                    className="search-submit"
                                    onClick={handleSearch}
                                >
                                    <span className="blind">검색</span>
                                    <i className="fa-solid fa-magnifying-glass"></i>
                                </button>
                            </div>
                        </div>
                        <div className="sort-wrap">
                            <p className="total">
                                총 <span className="point">{jobList.length}</span>개
                            </p>
                            <button
                                type="button"
                                className="filter-btn"
                                onClick={() => setIsModalOpen(true)}
                            >
                                <i className="fa-solid fa-sliders"></i>필터
                            </button>
                            <FilterModal
                                isOpen={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                                filters={filters}
                                onFilterChange={setFilters}
                                onFilterApply={handleFilterApply}
                                onFilterReset={handleFilterReset}
                            />
                        </div>
                    </div>
                </section>

                <section>
                    <div className="inner-box">
                        {jobList.map((job: any) => (
                            <div key={job.jobPostingCode} className="round-box">
                                <div className="round-body">
                                    <div className={`flag ${job.workType === 'HOURLY' ? '' : 'gray'}`}>
                                        {job.workType === 'HOURLY' ? '시간제' : '종일제'}
                                    </div>
                                    <div className="col-info">
                                        <div className="tag-list">
                                            <span className="tag color">{job.serviceType}</span>
                                            <span className="tag">{job.address}</span>
                                            <span className="tag">{job.gender}</span>
                                            <span className="tag">{job.patientAge}세</span>
                                            <span className="tag">{job.carePlaceType}</span>
                                        </div>
                                        <h3 className="location-tit">{job.hospitalName || job.address}</h3>
                                        <p className="during">
                                            {job.careStartDate} ~ {job.careEndDate} ({job.careDayCount}일)
                                        </p>
                                    </div>
                                    <div className="info-row top-line">
                                        <span className="label">
                                            돌봄 급여 <span className={job.payNegotiable === 'Y' ? '' : 'point'}>
                                                ({job.payNegotiable === 'Y' ? '제안 가능' : '제안 불가능'})
                                            </span>
                                        </span>
                                        <span className="cont ico">
                                            <i className="fa-solid fa-coins"></i>
                                            {job.workType === 'HOURLY' ? '시급' : '일급'} {job.dailyPay?.toLocaleString()}원
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <span className="label">지원 마감</span>
                                        <span className="cont ico">
                                            <i className="fa-regular fa-clock"></i>{getDeadlineDisplay(job)}
                                        </span>
                                    </div>
                                    <div className="info-row foot mt10">
                                        <div className="foot-line-btn w100 text-center"
                                             onClick={() => handleDetailClick(job.jobPostingCode)}
                                        >
                                            <i className="fa-solid fa-magnifying-glass"></i>자세히 보기
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </main>
        </div>
    );
}